import { useLanguage } from '../i18n/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  const { language, isRTL } = useLanguage();

  const content = {
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last Updated: January 2026',
      sections: [
        {
          heading: '1. Information We Collect',
          text: `We collect information you provide directly to us, such as when you fill out a contact form, request a quote, or communicate with us via WhatsApp or email. This may include:
          
• Name and contact information (phone number, email address)
• Project details and requirements
• Location and address information
• Any other information you choose to provide`
        },
        {
          heading: '2. How We Use Your Information',
          text: `We use the information we collect to:

• Respond to your inquiries and provide customer service
• Process and fulfill your service requests
• Send you project updates and related communications
• Improve our services and website experience
• Comply with legal obligations`
        },
        {
          heading: '3. Information Sharing',
          text: `We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:

• With your consent
• To comply with legal obligations
• To protect our rights and safety
• With service providers who assist in our operations`
        },
        {
          heading: '4. Data Security',
          text: `We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.`
        },
        {
          heading: '5. Your Rights',
          text: `You have the right to:

• Access the personal information we hold about you
• Request correction of inaccurate information
• Request deletion of your personal information
• Opt-out of marketing communications`
        },
        {
          heading: '6. Cookies',
          text: `Our website may use cookies to enhance your browsing experience. You can control cookie settings through your browser preferences.`
        },
        {
          heading: '7. Contact Us',
          text: `If you have any questions about this Privacy Policy, please contact us:

• Email: info@glow-space.com
• Phone: +971 56 683 2840
• Address: Champion Tower, Dubai Sports City, Dubai, UAE`
        }
      ]
    },
    ar: {
      title: 'سياسة الخصوصية',
      lastUpdated: 'آخر تحديث: يناير 2026',
      sections: [
        {
          heading: '1. المعلومات التي نجمعها',
          text: `نجمع المعلومات التي تقدمها لنا مباشرة، مثل عندما تملأ نموذج اتصال أو تطلب عرض سعر أو تتواصل معنا عبر واتساب أو البريد الإلكتروني. قد يشمل ذلك:

• الاسم ومعلومات الاتصال (رقم الهاتف، عنوان البريد الإلكتروني)
• تفاصيل المشروع ومتطلباته
• معلومات الموقع والعنوان
• أي معلومات أخرى تختار تقديمها`
        },
        {
          heading: '2. كيف نستخدم معلوماتك',
          text: `نستخدم المعلومات التي نجمعها من أجل:

• الرد على استفساراتك وتقديم خدمة العملاء
• معالجة وتنفيذ طلبات الخدمة الخاصة بك
• إرسال تحديثات المشروع والاتصالات ذات الصلة
• تحسين خدماتنا وتجربة الموقع
• الامتثال للالتزامات القانونية`
        },
        {
          heading: '3. مشاركة المعلومات',
          text: `نحن لا نبيع أو نتاجر أو نؤجر معلوماتك الشخصية لأطراف ثالثة. قد نشارك معلوماتك فقط في الظروف التالية:

• بموافقتك
• للامتثال للالتزامات القانونية
• لحماية حقوقنا وسلامتنا
• مع مقدمي الخدمات الذين يساعدون في عملياتنا`
        },
        {
          heading: '4. أمان البيانات',
          text: `نطبق تدابير أمنية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو التعديل أو الكشف أو التدمير. ومع ذلك، لا توجد طريقة نقل عبر الإنترنت آمنة بنسبة 100٪.`
        },
        {
          heading: '5. حقوقك',
          text: `لديك الحق في:

• الوصول إلى المعلومات الشخصية التي نحتفظ بها عنك
• طلب تصحيح المعلومات غير الدقيقة
• طلب حذف معلوماتك الشخصية
• إلغاء الاشتراك في الاتصالات التسويقية`
        },
        {
          heading: '6. ملفات تعريف الارتباط',
          text: `قد يستخدم موقعنا ملفات تعريف الارتباط لتحسين تجربة التصفح الخاصة بك. يمكنك التحكم في إعدادات ملفات تعريف الارتباط من خلال تفضيلات المتصفح.`
        },
        {
          heading: '7. اتصل بنا',
          text: `إذا كان لديك أي أسئلة حول سياسة الخصوصية هذه، يرجى الاتصال بنا:

• البريد الإلكتروني: info@glow-space.com
• الهاتف: +971 56 683 2840
• العنوان: برج تشامبيون، مدينة دبي الرياضية، دبي، الإمارات`
        }
      ]
    }
  };

  const t = content[language];
  const fontFamily = isRTL ? "'Noto Sans Arabic', sans-serif" : "'Manrope', sans-serif";
  const headingFont = isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif";

  return (
    <div className="min-h-screen bg-[#060B14]" data-testid="privacy-policy-page">
      <Navbar />
      
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 
            className={`text-4xl md:text-5xl font-bold text-white mb-4 ${isRTL ? 'text-right' : ''}`}
            style={{ fontFamily: headingFont }}
          >
            {t.title}
          </h1>
          <p className={`text-slate-400 mb-12 ${isRTL ? 'text-right' : ''}`} style={{ fontFamily }}>
            {t.lastUpdated}
          </p>

          <div className="space-y-8">
            {t.sections.map((section, index) => (
              <div key={index} className={isRTL ? 'text-right' : ''}>
                <h2 
                  className="text-xl font-semibold text-white mb-4"
                  style={{ fontFamily: headingFont }}
                >
                  {section.heading}
                </h2>
                <div 
                  className="text-slate-300 leading-relaxed whitespace-pre-line"
                  style={{ fontFamily }}
                >
                  {section.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
